import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Menu, X, Monitor, Smartphone,
  Usb, Scan, ShieldCheck, Users, Activity,
  Download, LogOut, User as UserIcon, Key, FolderSearch, Upload,
  ChevronDown, ChevronRight, Globe, BarChart3, DollarSign, Bell, AlertTriangle, Settings, Mail, Check, Bot, ReceiptText, BookOpen, KeyRound, Clock, UserCog, Wallet
} from "lucide-react";

import "./sidenav.css";
import Logo from "../../assets/gca.png";
import { getRole, logoutUser } from "../../utils/authService";
import { apiGet, apiPost, apiPut } from "../../utils/api";
import { toast } from "../../utils/toast";
import socket from "../../utils/socket";

const ALERTS_LAST_SEEN_KEY = "sidebar_alerts_last_seen_at";
const ALERTS_RECENT_WINDOW_MS = 14 * 60 * 60 * 1000;
const FEATURE_SYNC_INTERVAL_MS = 10000;

function areUnlockedFeatureMapsEqual(left = {}, right = {}) {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => key === rightKeys[index] && !!left[key] === !!right[key]);
}

function buildUnlockedFeatureMap(unlockedFeatureIds = []) {
  const unlockedMap = {};
  if (Array.isArray(unlockedFeatureIds)) {
    unlockedFeatureIds.forEach((id) => {
      const normalizedId = String(id || "").trim();
      if (normalizedId) unlockedMap[normalizedId] = true;
    });
  }
  return unlockedMap;
}

const Sidebar = ({ onToggle, isOpen: controlledIsOpen }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (val) => {
    if (controlledIsOpen !== undefined) {
      if (onToggle) onToggle(val);
    } else {
      setInternalIsOpen(val);
      if (onToggle) onToggle(val);
    }
  };

  const role = getRole();
  const name = sessionStorage.getItem("name") || "User";
  const email = sessionStorage.getItem("email");
  const displayRole = sessionStorage.getItem("displayRole") || "";
  // "Download Account" is a seeded role:"client" user meant only to log in and grab the
  // agent installer — it must not get the full client experience (settings, billing,
  // notification center, etc.) just because it shares the "client" role value.
  // Fall back to matching the email prefix because accounts seeded before the
  // displayRole field existed never got it backfilled in the database.
  const isDownloadOnlyAccount = displayRole === "Download Account" || (email || "").toLowerCase().startsWith("download@");
  const canManageDailyReportSchedule = role === "client" && !isDownloadOnlyAccount;
  const [tenantName, setTenantName] = useState(sessionStorage.getItem("tenantName") || "");
  const allowedViews = (() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem("allowedViews") || "[]");
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  })();
  const allowedViewSet = new Set(allowedViews);

  const [unlockedFeatures, setUnlockedFeatures] = useState({});
  // Whether the tenant has any active/trial subscription at all (distinct from
  // which specific features that plan unlocks). Defaults to false, same as
  // unlockedFeatures defaulting to {} — items reveal once the fetch resolves
  // instead of flashing visible-then-hidden for tenants with no subscription.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [subscriptionMap, setSubscriptionMap] = useState({});
  const [subscriptionSavingType, setSubscriptionSavingType] = useState("");
  const [simulatingNotifications, setSimulatingNotifications] = useState(false);
  const [reportSchedule, setReportSchedule] = useState(() => ({
    enabled: true,
    time: "09:00",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    lastStatus: "",
    lastError: "",
    lastSentAt: null,
    lastAttemptedAt: null,
    lastPlanId: "",
  }));
  const [reportRecipientEmail, setReportRecipientEmail] = useState("");
  const [reportScheduleEligibility, setReportScheduleEligibility] = useState(() => ({
    isEligible: true,
    reasonCode: "",
    message: "",
    activePlanName: "",
  }));
  const [reportScheduleSaving, setReportScheduleSaving] = useState(false);
  const [emailMasterEnabled, setEmailMasterEnabled] = useState(false);
  const [emailMasterSaving, setEmailMasterSaving] = useState(false);
  const [alertsLastSeenAt, setAlertsLastSeenAt] = useState(() => Number(localStorage.getItem(ALERTS_LAST_SEEN_KEY) || 0));
  const [showCfoConfigNav, setShowCfoConfigNav] = useState(() => localStorage.getItem("cfoSalaryConfigNavEnabled") === "1");

  const accountAlertsTriggerRef = useRef(null);
  const alertsPanelRef = useRef(null);
  const notificationSettingsModalRef = useRef(null);


  const applyUnlockedFeatures = React.useCallback((nextMap, { broadcast = false } = {}) => {
    if (!nextMap || typeof nextMap !== "object") return;
    setUnlockedFeatures((current) => {
      if (areUnlockedFeatureMapsEqual(current, nextMap)) return current;
      localStorage.setItem("unlockedFeatures", JSON.stringify(nextMap));
      if (broadcast) {
        window.dispatchEvent(
          new CustomEvent("features:updated", {
            detail: { unlockedFeatures: nextMap },
          })
        );
      }
      return nextMap;
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchUnlockedFeatures = async () => {
      try {
        const response = await apiGet("/api/features");
        if (response.ok && isMounted) {
          const data = await response.json();
          applyUnlockedFeatures(buildUnlockedFeatureMap(data?.unlockedFeatures), { broadcast: true });
          setHasActiveSubscription(!!String(data?.currentPlanId || "").trim());
        }
      } catch (err) {
        console.error("Failed to fetch sidebar features:", err);
      }
    };

    void fetchUnlockedFeatures();
    const interval = setInterval(() => {
      void fetchUnlockedFeatures();
    }, FEATURE_SYNC_INTERVAL_MS);
    const handleFocusSync = () => {
      void fetchUnlockedFeatures();
    };
    window.addEventListener("focus", handleFocusSync);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener("focus", handleFocusSync);
    };
  }, [applyUnlockedFeatures]);

  // Fetch tenant name once on mount for scoped roles that don't have it in sessionStorage
  useEffect(() => {
    if (tenantName) return;
    let active = true;
    apiGet("/api/auth/me")
      .then((res) => res.json())
      .then((data) => {
        const name = data?.tenantId?.name || data?.companyName || "";
        if (name && active) {
          sessionStorage.setItem("tenantName", name);
          setTenantName(name);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Effect to listen for storage changes (triggered by features.jsx)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("unlockedFeatures");
      if (saved) {
        try {
          applyUnlockedFeatures(JSON.parse(saved));
        } catch {
          // Ignore malformed cache and wait for next backend sync.
        }
      }
      setShowCfoConfigNav(localStorage.getItem("cfoSalaryConfigNavEnabled") === "1");
    };
    const handleFeatureUpdate = (event) => {
      const nextMap = event?.detail?.unlockedFeatures;
      if (nextMap && typeof nextMap === "object") {
        applyUnlockedFeatures(nextMap);
      } else {
        handleStorageChange();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("features:updated", handleFeatureUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("features:updated", handleFeatureUpdate);
    };
  }, [applyUnlockedFeatures]);

  useEffect(() => {
    if (location.pathname === "/cfo-salary-config" && !showCfoConfigNav) {
      localStorage.setItem("cfoSalaryConfigNavEnabled", "1");
      setShowCfoConfigNav(true);
    }
  }, [location.pathname, showCfoConfigNav]);

  useEffect(() => {
    const handleCfoNavEnable = () => setShowCfoConfigNav(true);
    window.addEventListener("cfo-config-nav-enabled", handleCfoNavEnable);
    return () => window.removeEventListener("cfo-config-nav-enabled", handleCfoNavEnable);
  }, []);

  // Order follows the requested sidebar spec: Dashboard, Devices, Monitor,
  // Attendance, Licenses, Settings, (Billing — kept, not in the spec's list but not
  // asked to be removed either), Agent, Guide. VisuN AI is force-pinned to the
  // very top by moveToFront below, ahead of Dashboard.
  const clientNavItems = [
    // Merged Executive/CFO/Team dashboard hub — DashboardHub.jsx filters its own
    // tabs, and always has at least "Team" available to a client, so this item
    // is unconditional like Billing/Guide rather than gated on one view key.
    { label: "Dashboard", path: "/dashboard", icon: <BarChart3 size={20} />, id: "dashboard", alwaysShow: true },
    { label: "VisuN AI", path: "/visun-ai", icon: <Bot size={20} />, id: "ai", viewKey: "visun_ai" },
    { label: "Devices", path: "/devices", icon: <Smartphone size={20} />, id: "devices", viewKey: "devices" },
    { label: "Monitor", path: "/monitoring", icon: <Activity size={20} />, id: "monitoring", viewKey: "monitoring" },
    { label: "Attendance", path: "/all-attendance", icon: <Clock size={20} />, id: "attendance", viewKey: "monitoring" },
    { label: "Settings", path: "/settings", icon: <Settings size={20} />, id: "settings", viewKey: "productivity_config" },
    { label: "License Manager", path: "/license", icon: <KeyRound size={20} />, requiresSubscription: true },
    { label: "Plans & Billing", path: "/billing", icon: <DollarSign size={20} />, alwaysShow: true, viewKey: "billing" },
    // No `id` on purpose — unlike Devices/Monitoring/etc., Agent (formerly "Download") must render
    // regardless of feature-unlock state (see App.jsx's /download route comment: "accessible
    // regardless of plan/feature-unlock status"). It previously reused id:"devices", which for
    // SCOPED_ROLES like "employee" (whose allowedFeatures defaults to [], see ssoShared.js) meant
    // unlockedFeatures.devices was always false regardless of the tenant's actual plan — silently
    // hiding it from every employee account, SSO or not.
    { label: "Download", path: "/download", icon: <Download size={20} />, viewKey: "download" },
    { label: "Guide", path: "/guide", icon: <BookOpen size={20} />, alwaysShow: true, viewKey: "guide" },
  ];

  // KAN-91: Role-specific nav items for scoped roles
  // Profile/Manage Accounts lives in the TopNav account dropdown for every role now,
  // not in these lists. Employee: own dashboard only.
  const employeeNavItems = [
    { label: "Dashboard", path: "/dashboard", icon: <BarChart3 size={20} />, alwaysShow: true },
  ];

  // Manager: team dashboard only (can be extended by tenant admin via allowedViews)
  const managerNavItems = [
    { label: "Dashboard", path: "/dashboard", icon: <Activity size={20} />, alwaysShow: true },
    // Default-on like Team Dashboard, not gated behind allowedViews/unlockedFeatures —
    // a manager should always be able to reach their team's attendance. The page itself
    // (AllAttendance.jsx) is what actually scopes the data to their team, not this gate.
    { label: "Attendance", path: "/all-attendance", icon: <Clock size={20} />, alwaysShow: true },
  ];

  // Nav items for analytics_viewer and other viewer roles — no Download, no billing
  const viewerNavItems = [
    { label: "Dashboard", path: "/dashboard", icon: <BarChart3 size={20} />, alwaysShow: true },
    { label: "VisuN AI", path: "/visun-ai", icon: <Bot size={20} />, id: "ai", viewKey: "visun_ai" },
    { label: "Devices", path: "/devices", icon: <Monitor size={20} />, id: "devices", viewKey: "devices" },
    { label: "Attendance", path: "/all-attendance", icon: <Clock size={20} />, id: "attendance", viewKey: "monitoring" },
  ];

  // Paths that scoped roles (employee/manager) must never see regardless of allowedViews.
  const SCOPED_ROLE_BLOCKED_PATHS = new Set(["/download", "/billing"]);

  // SSO-JIT employee/manager accounts are granted "download" in allowedViews by default
  // (see ssoShared.js's SSO_DEFAULT_VIEWS_BY_ROLE) so they can self-serve the agent
  // installer — there's no admin-driven manual flow that hands it to them another way.
  // Manually-created employee/manager accounts never get "download" by default, but a
  // custom role template theoretically could, so this authProvider check is the actual
  // enforcement point keeping the carve-out SSO-only, not just the allowedViews grant.
  const isSsoAccount = (() => {
    const provider = sessionStorage.getItem("authProvider");
    return !!provider && provider !== "local";
  })();

  // Pin VisuN AI to the top of whatever nav list a role ends up with — it's
  // assembled differently per role (base item, allowedViews-merged, etc.), so
  // reordering once here beats repeating the same splice in every branch above.
  const moveToFront = (items, path) => {
    const idx = items.findIndex((item) => item.path === path);
    if (idx <= 0) return items;
    const reordered = [...items];
    const [found] = reordered.splice(idx, 1);
    return [found, ...reordered];
  };

  // Pick the correct nav list for the current role.
  // For scoped roles (employee / manager) the result is the role-specific BASE items
  // MERGED with any additional clientNavItems that allowedViews grants — minus Download and Billing.
  // This preserves manual configuration: if a tenant admin grants a manager access to "ceo_dashboard"
  // via a role template, that item will appear on top of the manager's default nav items.
  const downloadOnlyNavItems = [
    { label: "Download", path: "/download", icon: <Download size={20} />, alwaysShow: true },
  ];

  const getRoleNavItems = () => {
    if (role === "admin") return adminNavItems;
    if (isDownloadOnlyAccount) return downloadOnlyNavItems;
    if (role === "client") return clientNavItems;

    // Employee/manager have a fixed dashboard-scoped base list and must never see
    // Download or Billing, even if a role template were to grant those view keys.
    if (role === "employee" || role === "manager") {
      const baseItems = role === "employee" ? employeeNavItems : managerNavItems;
      const basePathSet = new Set(baseItems.map((item) => item.path));
      const additionalItems = clientNavItems.filter((item) => {
        const isSsoDownloadException = item.path === "/download" && isSsoAccount;
        if (SCOPED_ROLE_BLOCKED_PATHS.has(item.path) && !isSsoDownloadException) return false;
        if (basePathSet.has(item.path)) return false;
        if (!item.viewKey) return false;
        return allowedViewSet.has(item.viewKey);
      });
      // Download always renders last, below every other link — same spot it occupies for
      // every other role — regardless of where it falls in clientNavItems' own ordering or
      // how many other views a role template happens to grant on top of it.
      const downloadIdx = additionalItems.findIndex((item) => item.path === "/download");
      if (downloadIdx !== -1 && downloadIdx !== additionalItems.length - 1) {
        const [downloadItem] = additionalItems.splice(downloadIdx, 1);
        additionalItems.push(downloadItem);
      }
      return [...baseItems, ...additionalItems];
    }

    // ceo, cfo, analytics_viewer, and any other custom role template (e.g. a
    // tenant-defined "Admin" role) behave like a restricted client: universal
    // alwaysShow items (License Manager, ...) always show, but Plans & Billing
    // and Guide must be explicitly granted via the role template's allowedViews
    // like any other selectable view, and anything else shows only if granted —
    // including Download, since it's selectable when creating a role.
    const basePathSet = new Set(viewerNavItems.map((item) => item.path));
    const additionalItems = clientNavItems.filter((item) => {
      if (basePathSet.has(item.path)) return false;
      if (item.alwaysShow && item.path !== "/billing" && item.path !== "/guide") return true;
      if (!item.viewKey) return false;
      return allowedViewSet.has(item.viewKey);
    });

    return [...viewerNavItems, ...additionalItems];
  };

  // Filter navigation items to only show unlocked features.
  // For scoped roles the base items already have alwaysShow:true so they always pass.
  // Additional merged items are viewKey-gated and will pass the allowedViewSet check below.
  const getVisibleNavItems = (items) => {
    if (role === "admin") return items;
    if (role === "client") {
      return items.filter(item => {
        // Visible whenever ANY plan is active, regardless of per-feature unlock
        // state — but hidden entirely when there's no active subscription.
        if (item.requiresSubscription) return hasActiveSubscription;
        if (item.alwaysShow) return true;
        if (item.children) {
          return item.children.some(child => child.alwaysShow || (child.id ? unlockedFeatures[child.id] : true));
        }
        // No `id` means the item isn't tied to any purchasable feature (e.g. Download) —
        // nothing to check against unlockedFeatures, so it passes once view-permission clears.
        return item.id ? unlockedFeatures[item.id] : true;
      });
    }
    // Scoped roles (employee / manager / viewer): every item must either be
    // alwaysShow OR have its viewKey present in allowedViews AND its feature unlocked.
    return items.filter(item => {
      if (item.alwaysShow) return true;
      const viewAllowed = item.viewKey ? allowedViewSet.has(item.viewKey) : false;
      if (!viewAllowed) return false;
      if (item.children) {
        return item.children.some(child => {
          if (child.alwaysShow) return true;
          const childViewAllowed = child.viewKey ? allowedViewSet.has(child.viewKey) : false;
          return childViewAllowed && (child.id ? unlockedFeatures[child.id] : true);
        });
      }
      return item.id ? unlockedFeatures[item.id] : true;
    });
  };


  // Toggle expanded menu
  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Handle navigation click (close sidebar on mobile)
  const handleNavClick = () => {
    if (window.innerWidth < 1024) setIsOpen(false);
  };


  const adminNavItems = [
    { label: "Admin Dashboard", path: "/admin/dashboard", icon: <ShieldCheck size={20} /> },
    { label: "Billing Overview", path: "/admin/billing-overview", icon: <ReceiptText size={20} /> },
    { label: "Manage Tenants", path: "/admin/tenants", icon: <Users size={20} /> },
    { label: "License Manager", path: "/admin/licenses", icon: <Key size={20} /> },
    { label: "Price Manager", path: "/admin/pricing", icon: <DollarSign size={20} /> },
  ];

  const handleLogout = () => {
    logoutUser();
    navigate("/login");
  };

  const getAlertTimestamp = (alert) => {
    const dateValue = alert?.time || alert?.lastVisted || alert?.updatedAt || alert?.createdAt;
    const parsed = new Date(dateValue).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatAlertTime = (alert) => {
    const dateValue = alert?.time || alert?.lastVisted || alert?.updatedAt || alert?.createdAt;
    if (!dateValue) return "Unknown time";
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "Unknown time";
    return parsedDate.toLocaleString();
  };

  const formatRelativeTime = (alert) => {
    const timestamp = getAlertTimestamp(alert);
    if (!timestamp) return "Unknown";
    const deltaMs = Math.max(0, Date.now() - timestamp);
    const deltaMinutes = Math.floor(deltaMs / 60000);
    if (deltaMinutes < 1) return "Just now";
    if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
    const deltaHours = Math.floor(deltaMinutes / 60);
    if (deltaHours < 24) return `${deltaHours}h ago`;
    const deltaDays = Math.floor(deltaHours / 24);
    return `${deltaDays}d ago`;
  };

  const getSeverityClass = (severity) => {
    const sev = String(severity || "").toLowerCase();
    if (sev === "critical") return "sev-critical";
    if (sev === "high" || sev === "error" || sev === "failure") return "sev-high";
    if (sev === "medium" || sev === "warning") return "sev-medium";
    return "sev-low";
  };

  const getAlertRoute = (alert) => {
    const type = String(alert?.type || "").toLowerCase();
    if (type.includes("unsafe web")) return "/url-tracker";
    if (type.includes("unauthorized usb")) return "/usb";
    if (type.includes("file manipulation")) return "/file-monitor";
    if (type.includes("data exfiltration")) return "/file-uploads";
    if (type.includes("productivity analysis")) return "/dashboard";
    return "/dashboard";
  };

  const handleAlertItemClick = (alert) => {
    setSelectedAlert(alert);
  };

  const handleAlertNavigate = () => {
    if (!selectedAlert) return;
    const route = getAlertRoute(selectedAlert);
    setSelectedAlert(null);
    setIsAlertsOpen(false);
    navigate(route);
  };

  const unreadAlertCount = alerts.reduce(
    (count, alert) => count + (getAlertTimestamp(alert) > alertsLastSeenAt ? 1 : 0),
    0
  );
  const sortedAlerts = [...alerts].sort((a, b) => getAlertTimestamp(b) - getAlertTimestamp(a));
  const notificationTypeCatalog = [
    "Unsafe Web Activity",
    "Restricted App",
    "File Manipulation",
    "Unauthorized USB",
    "Data Exfiltration",
    "Agent Health",
  ];
  const notificationTypeRows = notificationTypeCatalog.map((type) => ({
    type,
    count: sortedAlerts.filter((alert) => String(alert?.type || "").toLowerCase() === type.toLowerCase()).length,
    subscribed: !!subscriptionMap[type],
  }));
  const isValidScheduleTime = (value) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(value || "").trim());
  const isValidEmailAddress = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim().toLowerCase());
  const readResponseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };
  const formatScheduleStatusLabel = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return "";
    if (normalized === "sent") return "Sent";
    if (normalized === "duplicate") return "Already sent";
    if (normalized === "ineligible") return "Plan not eligible";
    if (normalized === "recipient_missing") return "Recipient missing";
    if (normalized === "audit_user_missing") return "Owner missing";
    if (normalized === "mail_not_sent") return "Mail not configured";
    if (normalized === "send_error") return "Send failed";
    if (normalized === "failed") return "Dispatch failed";
    return normalized.replace(/_/g, " ");
  };
  const formatScheduleDateTime = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleString();
  };
  const recentAlerts = sortedAlerts.filter((alert) => {

    const timestamp = getAlertTimestamp(alert);
    return timestamp > 0 && (Date.now() - timestamp) <= ALERTS_RECENT_WINDOW_MS;
  });

  const visibleAlerts = showAllAlerts ? sortedAlerts : recentAlerts;

  const markAlertsAsSeen = () => {
    const latestAlertTime = alerts.reduce(
      (max, alert) => Math.max(max, getAlertTimestamp(alert)),
      0
    );
    const nextSeenAt = Math.max(latestAlertTime, Date.now());
    setAlertsLastSeenAt(nextSeenAt);
    localStorage.setItem(ALERTS_LAST_SEEN_KEY, String(nextSeenAt));
  };

  const handleAlertsToggle = () => {
    setIsAlertsOpen((prev) => {
      const next = !prev;
      if (next) {
        setShowAllAlerts(false);
        markAlertsAsSeen();
      }
      return next;
    });
  };

  useEffect(() => {
    if (onToggle) onToggle(isOpen);
  }, [isOpen, onToggle]);

  useEffect(() => {
    if (controlledIsOpen !== undefined) return undefined;
    const handleResize = () => {
      if (window.innerWidth < 1024) setIsOpen(false);
      else setIsOpen(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [controlledIsOpen]);

  useEffect(() => {
    if (isDownloadOnlyAccount) return;
    let isMounted = true;

    const mapSecurityThreatsToAlerts = (threats = []) =>
      threats.map((threat, idx) => ({
        _id: threat?._id || `security-${idx}-${threat?.agentId || "agent"}-${String(threat?.time || "")}`,
        type: threat?.type || "Security Alert",
        severity: threat?.severity || "High",
        agentId: threat?.agentId || "Unknown agent",
        title: threat?.type || "Security Alert",
        description: threat?.description || "Security event detected",
        time: threat?.time,
      }));

    const mapUrlThreatsToAlerts = (threats = []) =>
      threats.map((threat, idx) => ({
        _id: threat?._id || `url-${idx}-${threat?.agentId || "agent"}-${String(threat?.lastVisted || threat?.updatedAt || "")}`,
        type: "Unsafe Web Activity",
        severity: threat?.alert === "THREAT_DETECTED" ? "Critical" : "High",
        agentId: threat?.agentId || "Unknown agent",
        title: threat?.url || "Threat detected",
        description: threat?.title || "Suspicious activity detected in URL tracker",
        time: threat?.lastVisted || threat?.updatedAt || threat?.createdAt,
      }));

    const mapDailyProductivityAlert = (analyticsData, dateStr) => {
      const summary = analyticsData?.summary || {};
      const totalEmployees = Number(summary?.totalEmployees || 0);
      const activeEmployees = Number(summary?.activeEmployees || 0);
      const productivityPct = Number(summary?.productivityPct || 0);
      const avgUtilization = Number(summary?.avgUtilization || 0);
      const focusScore = Number(summary?.focusScore || 0);

      return {
        _id: `productivity-${dateStr}`,
        type: "Productivity Analysis",
        severity: productivityPct >= 75 ? "Low" : productivityPct >= 55 ? "Medium" : "High",
        agentId: "Organization",
        title: `Daily analytics snapshot (${dateStr})`,
        description: `Team ${activeEmployees}/${totalEmployees} active | Productivity ${productivityPct.toFixed(1)}% | Utilization ${avgUtilization.toFixed(1)}% | Focus ${focusScore}`,
        // Daily alert anchored at 00:00 for the selected date.
        time: `${dateStr}T00:00:00.000Z`,
      };
    };

    const mapHealthEventsToAlerts = (events = []) =>
      events.map((e) => {
        const stoppedAt     = e.stoppedAt   ? new Date(e.stoppedAt)   : new Date(e.createdAt);
        const recoveredAt   = e.recoveredAt ? new Date(e.recoveredAt) : null;
        const agentLabel    = e.hostname || e.agentId;
        const downtimeLabel = (() => {
          if (e.event !== "recovered" || !e.downtimeSecs) return "";
          const mins = Math.floor(e.downtimeSecs / 60);
          const secs = e.downtimeSecs % 60;
          return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
        })();
        return {
          _id:          `agent-health-${e._id}`,
          type:         "Agent Health",
          severity:     e.event === "stopped" ? "Critical" : "Low",
          agentId:      e.agentId,
          hostname:     e.hostname,
          title:        e.event === "stopped"
            ? `Agent stopped — ${agentLabel}`
            : `Agent recovered — ${agentLabel}`,
          description:  e.event === "stopped"
            ? `Agent "${agentLabel}" went offline at ${stoppedAt.toLocaleString()}.`
            : `Agent "${agentLabel}" is back online. Downtime: ${downtimeLabel || "unknown"}. Recovered at ${recoveredAt?.toLocaleString() ?? "unknown"}.`,
          time:         stoppedAt.toISOString(),
          dedupeKey:    `agent-health|${e.agentId}|${e.event}|${stoppedAt.toISOString()}`,
          source:       "gca",
          downtimeSecs: e.downtimeSecs ?? null,
          stoppedAt:    stoppedAt.toISOString(),
          recoveredAt:  recoveredAt?.toISOString() ?? null,
        };
      });

    const fetchAlerts = async () => {
      try {
        if (isMounted) setAlertsLoading(true);
        const todayStr = new Date().toISOString().split("T")[0];
        const [securityResponse, urlResponse] = await Promise.all([
          apiGet("/api/analytics/security-pulse?filter=weekly"),
          apiGet("/api/url-tracker/alerts"),
        ]);

        let securityAlerts = [];
        if (securityResponse.ok) {
          const data = await securityResponse.json();
          securityAlerts = mapSecurityThreatsToAlerts(Array.isArray(data?.threats) ? data.threats : []);
        }

        let urlAlerts = [];
        if (urlResponse.ok) {
          const data = await urlResponse.json();
          urlAlerts = mapUrlThreatsToAlerts(Array.isArray(data?.alerts) ? data.alerts : []);
        }

        let productivityAlert = null;
        try {
          const analyticsResponse = await apiGet(`/api/analytics/ceo-dashboard?filter=daily&date=${todayStr}`);
          if (analyticsResponse.ok) {
            const analyticsData = await analyticsResponse.json();
            if (analyticsData?.success) {
              productivityAlert = mapDailyProductivityAlert(analyticsData, todayStr);
            }
          }
        } catch (err) {
          console.error("Failed to fetch productivity summary for notifications:", err);
        }

        let healthAlerts = [];
        try {
          const healthResponse = await apiGet("/api/supervisor/health-events?limit=50");
          if (healthResponse.ok) {
            const data = await healthResponse.json();
            healthAlerts = mapHealthEventsToAlerts(Array.isArray(data?.events) ? data.events : []);
          }
        } catch (err) {
          console.error("Failed to fetch agent health events:", err);
        }

        const merged = [
          ...securityAlerts,
          ...urlAlerts,
          ...healthAlerts,
          ...(productivityAlert ? [productivityAlert] : []),
        ]
          .sort((a, b) => getAlertTimestamp(b) - getAlertTimestamp(a))
          .slice(0, 100);

        if (isMounted) {
          setAlerts(merged);
        }
      } catch (err) {
        console.error("Failed to fetch sidebar alerts:", err);
      } finally {
        if (isMounted) setAlertsLoading(false);
      }
    };

    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);

    // ⭐ REAL-TIME: Listen for security_alert events from the backend socket broadcast.
    // Without this, the sidebar only refreshed on the 30-second poll cycle.
    // broadcastAlertToTenant() fires this event immediately when an alert is queued.
    const handleSocketAlert = (alert) => {
      if (!isMounted) return;
      setAlerts((prev) => {
        // Deduplicate by _id or dedupeKey to avoid showing the same alert twice
        const existingIds = new Set(prev.map((a) => String(a._id || "")));
        const dedupeKey = String(alert?.dedupeKey || "");
        const existingDedupes = new Set(prev.map((a) => String(a?.dedupeKey || "")));
        if (
          (alert?._id && existingIds.has(String(alert._id))) ||
          (dedupeKey && existingDedupes.has(dedupeKey))
        ) {
          return prev; // already have it
        }
        // Prepend and keep the list capped at 100
        return [alert, ...prev].slice(0, 100);
      });
    };
    socket.on("security_alert", handleSocketAlert);

    return () => {
      isMounted = false;
      clearInterval(interval);
      socket.off("security_alert", handleSocketAlert);
    };
  }, []);

  useEffect(() => {
    if (isDownloadOnlyAccount) return;
    let isMounted = true;
    const fetchNotificationSettings = async () => {
      // Fetch subscriptions independently so a failure here never blocks the rest.
      try {
        const subscriptionsResponse = await apiGet("/api/notifications/subscriptions");
        if (subscriptionsResponse.ok && isMounted) {
          const data = await subscriptionsResponse.json();
          const nextMap = {};
          const latest = {};
          (Array.isArray(data?.subscriptions) ? data.subscriptions : []).forEach((row) => {
            if (!row?.type) return;
            const ts = new Date(row.updatedAt || 0).getTime();
            if (!latest[row.type] || ts > latest[row.type]) {
              latest[row.type] = ts;
              nextMap[row.type] = !!row.subscribed;
            }
          });
          setSubscriptionMap(nextMap);
          setEmailMasterEnabled(!!data?.emailNotificationsEnabled);
        }
      } catch (err) {
        console.error("Failed to fetch notification subscriptions:", err);
      }

      // Fetch daily report schedule independently (client-only).
      if (canManageDailyReportSchedule && isMounted) {
        try {
          const scheduleResponse = await apiGet("/api/notifications/daily-report/schedule");
          if (scheduleResponse.ok && isMounted) {
            const scheduleData = await scheduleResponse.json();
            const schedule = scheduleData?.schedule || {};
            setReportSchedule((prev) => ({
              enabled: typeof schedule.enabled === "boolean" ? schedule.enabled : prev.enabled,
              time: isValidScheduleTime(schedule.time) ? schedule.time : prev.time,
              timezone: String(schedule.timezone || "").trim() || prev.timezone,
              lastStatus: String(schedule.lastStatus || "").trim().toLowerCase(),
              lastError: String(schedule.lastError || "").trim(),
              lastSentAt: schedule.lastSentAt || null,
              lastAttemptedAt: schedule.lastAttemptedAt || null,
              lastPlanId: String(schedule.lastPlanId || "").trim().toLowerCase(),
            }));
            setReportRecipientEmail(String(scheduleData?.recipientEmail || "").trim().toLowerCase());
            setReportScheduleEligibility({
              isEligible: scheduleData?.eligibility?.isEligible !== false,
              reasonCode: String(scheduleData?.eligibility?.reasonCode || "").trim(),
              message: String(scheduleData?.eligibility?.message || "").trim(),
              activePlanName: String(scheduleData?.eligibility?.activePlanName || "").trim(),
            });
          }
        } catch (err) {
          console.error("Failed to fetch daily report schedule:", err);
        }
      }
    };
    fetchNotificationSettings();
    return () => { isMounted = false; };
  }, [canManageDailyReportSchedule]);

  const handleToggleSubscription = async (type, subscribed) => {
    // Optimistic Update
    const previousMap = { ...subscriptionMap };
    setSubscriptionMap((prev) => ({ ...prev, [type]: subscribed }));

    try {
      setSubscriptionSavingType(type);
      const response = await apiPut("/api/notifications/subscriptions", { type, subscribed });

      if (!response.ok) {
        toast.error(`Failed to update ${type} setting`);
        setSubscriptionMap(previousMap); // Revert
        return;
      }

      const data = await response.json();
      const nextMap = {};
      (Array.isArray(data?.subscriptions) ? data.subscriptions : []).forEach((row) => {
        if (row?.type) nextMap[row.type] = !!row.subscribed;
      });
      setSubscriptionMap(nextMap);
      toast.success(`${type} notifications ${subscribed ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error("Failed to update notification subscription:", err);
      toast.error("Network error updating notification settings");
      setSubscriptionMap(previousMap); // Revert
    } finally {
      setSubscriptionSavingType("");
    }
  };

  const handleToggleEmailMaster = async () => {
    if (emailMasterSaving) return;
    const nextState = !emailMasterEnabled;
    const previousState = emailMasterEnabled;
    setEmailMasterEnabled(nextState);

    try {
      setEmailMasterSaving(true);
      const response = await apiPut("/api/notifications/tenant-email-toggle", { enabled: nextState });
      if (response.ok) {
        toast.success(`Email notifications ${nextState ? "enabled" : "paused"}`);
      } else {
        toast.error("Failed to update master toggle");
        setEmailMasterEnabled(previousState);
      }
    } catch (error) {
      console.error("Master toggle failed:", error);
      toast.error("Failed to update master toggle");
      setEmailMasterEnabled(previousState);
    } finally {
      setEmailMasterSaving(false);
    }
  };

  const handleSelectAllSubscriptions = async () => {
    const allSubscribed = notificationTypeCatalog.every(type => subscriptionMap[type]);
    const nextState = !allSubscribed;

    // Optimistic Update
    const previousMap = { ...subscriptionMap };
    const optimisticMap = {};
    notificationTypeCatalog.forEach(type => { optimisticMap[type] = nextState; });
    setSubscriptionMap(optimisticMap);

    try {
      setSubscriptionSavingType("all");
      const response = await apiPut("/api/notifications/subscriptions/bulk", { subscribed: nextState });
      if (!response.ok) {
        toast.error("Bulk update failed");
        setSubscriptionMap(previousMap);
        return;
      }

      const data = await response.json();
      const nextMap = {};
      (Array.isArray(data?.subscriptions) ? data.subscriptions : []).forEach((row) => {
        if (row?.type) nextMap[row.type] = !!row.subscribed;
      });
      setSubscriptionMap(nextMap);
      toast.success(`All notifications ${nextState ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error("Bulk update failed:", err);
      toast.error("Network error during bulk update");
      setSubscriptionMap(previousMap);
    } finally {
      setSubscriptionSavingType("");
    }
  };




  const handleSaveDailyReportSchedule = async () => {
    if (reportScheduleSaving) return;

    const timeValue = String(reportSchedule?.time || "").trim();
    if (!isValidScheduleTime(timeValue)) {
      toast.error("Enter a valid time in HH:mm format");
      return;
    }

    const timezoneValue = String(reportSchedule?.timezone || "").trim()
      || Intl.DateTimeFormat().resolvedOptions().timeZone
      || "UTC";
    const recipientEmailValue = String(reportRecipientEmail || "").trim().toLowerCase();

    if (recipientEmailValue && !isValidEmailAddress(recipientEmailValue)) {
      toast.error("Enter a valid company email address");
      return;
    }

    try {
      setReportScheduleSaving(true);
      const response = await apiPut("/api/notifications/daily-report/schedule", {
        enabled: !!reportSchedule.enabled,
        time: timeValue,
        timezone: timezoneValue,
        recipientEmail: recipientEmailValue,
      });
      const data = await readResponseJson(response);
      if (!response.ok || !data?.success) {
        toast.error(data?.message || "Failed to save daily report schedule");
        return;
      }

      const savedSchedule = data?.schedule || {};
      setReportSchedule((prev) => ({
        enabled: typeof savedSchedule.enabled === "boolean" ? savedSchedule.enabled : prev.enabled,
        time: isValidScheduleTime(savedSchedule.time) ? savedSchedule.time : timeValue,
        timezone: String(savedSchedule.timezone || "").trim() || timezoneValue,
        lastStatus: String(savedSchedule.lastStatus || "").trim().toLowerCase(),
        lastError: String(savedSchedule.lastError || "").trim(),
        lastSentAt: savedSchedule.lastSentAt || null,
        lastAttemptedAt: savedSchedule.lastAttemptedAt || null,
        lastPlanId: String(savedSchedule.lastPlanId || "").trim().toLowerCase(),
      }));

      setReportRecipientEmail(String(data?.recipientEmail || recipientEmailValue).trim().toLowerCase());
      setReportScheduleEligibility({
        isEligible: data?.eligibility?.isEligible !== false,
        reasonCode: String(data?.eligibility?.reasonCode || "").trim(),
        message: String(data?.eligibility?.message || "").trim(),
        activePlanName: String(data?.eligibility?.activePlanName || "").trim(),
      });

      toast.success(
        `Productivity report email scheduled at ${savedSchedule.time || timeValue}`
      );
    } catch (err) {
      console.error("Failed to save daily report schedule:", err);
      toast.error("Unable to save report schedule right now");
    } finally {
      setReportScheduleSaving(false);
    }
  };

  const dispatchAlertsForType = async (type) => {
    try {
      const typeAlerts = sortedAlerts.filter(
        (alert) => String(alert?.type || "").toLowerCase() === String(type || "").toLowerCase()
      );
      if (typeAlerts.length === 0) return;
      await apiPost("/api/notifications/dispatch", { alerts: typeAlerts });
    } catch (err) {
      console.error("Failed to dispatch type-based notification emails:", err);
    }
  };

  const handleSimulateAllNotifications = async () => {
    if (simulatingNotifications) return;

    const batchStartedAt = Date.now();
    const simulatedAlerts = notificationTypeCatalog.map((type, index) => ({
      _id: `sim-${batchStartedAt}-${index}`,
      type,
      severity: "Medium",
      agentId: "Simulation",
      title: `Simulated ${type}`,
      description: `Simulation batch ${batchStartedAt} generated this test alert for ${type}.`,
      time: new Date(batchStartedAt + index * 1000).toISOString(),
    }));

    try {
      setSimulatingNotifications(true);
      const response = await apiPost("/api/notifications/dispatch", {
        alerts: simulatedAlerts,
        recipientEmail: reportRecipientEmail,
      });
      const data = await readResponseJson(response);

      if (!response.ok || !data?.success) {
        toast.error(data?.message || "Failed to simulate notification emails");
        return;
      }

      const sent = Number(data?.sent || 0);
      const skipped = Number(data?.skipped || 0);
      const enabledTypes = notificationTypeRows.filter((row) => row.subscribed).length;
      const reasons = data?.skippedByReason || {};

      if (sent > 0) {
        toast.success(
          `Simulation completed: ${sent} sent, ${skipped} skipped. ${enabledTypes} notification type(s) are ON.`
        );
        return;
      }

      if (Number(reasons?.mail_not_sent || 0) > 0) {
        toast.error("No email sent. SMTP is not configured correctly on the server.");
        return;
      }

      if (Number(reasons?.not_subscribed_or_invalid_type || 0) > 0) {
        toast.info("No email sent. Turn ON at least one notification type and try again.");
        return;
      }

      if (Number(reasons?.email_missing || 0) > 0) {
        toast.error("No email sent. Your account email is missing.");
        return;
      }

      if (Number(reasons?.send_error || 0) > 0) {
        toast.error("No email sent due to SMTP send errors. Check backend logs.");
        return;
      }

      toast.info("Simulation completed, but no email was sent.");
    } catch (err) {
      console.error("Failed to simulate notification emails:", err);
      toast.error("Unable to simulate notifications right now");
    } finally {
      setSimulatingNotifications(false);
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideAccountTrigger = accountAlertsTriggerRef.current && accountAlertsTriggerRef.current.contains(event.target);
      const clickedInsidePanel = alertsPanelRef.current && alertsPanelRef.current.contains(event.target);
      const clickedInsideSettingsModal = notificationSettingsModalRef.current && notificationSettingsModalRef.current.contains(event.target);
      // Also check if click is on topnav bell button
      const clickedBell = event.target.closest && event.target.closest('#topnav-bell');
      if (!clickedInsideAccountTrigger && !clickedInsidePanel && !clickedInsideSettingsModal && !clickedBell) {
        setIsAlertsOpen(false);
        setIsNotificationSettingsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listen for TopNav bell button click
  useEffect(() => {
    const handleTopNavBell = () => handleAlertsToggle();
    window.addEventListener("toggle-notifications", handleTopNavBell);
    return () => window.removeEventListener("toggle-notifications", handleTopNavBell);
  }, [alerts, alertsLastSeenAt]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (selectedAlert) {
        setSelectedAlert(null);
        return;
      }
      if (isNotificationSettingsOpen) setIsNotificationSettingsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedAlert, isNotificationSettingsOpen]);

  return (
    <>
      {/* Toggle Button — always fixed, always on top */}
      <button className="sidebar-toggle" onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? "Close menu" : "Open menu"}>
        {isOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Backdrop — mobile only, closes sidebar on tap */}
      <div
        className={`sidebar-backdrop ${isOpen ? "visible" : ""}`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <aside className={`sidebar ${isOpen ? "open" : "closed"}`}>
        <div className="sidebar-top-section">
          {/* Logo */}
          <div className="sidebar-header">
            <img src={Logo} alt="GCA" className="sidebar-logo" />
            {isOpen && <h1 className="company-name">Global Cyber Associates</h1>}
          </div>

          {/* NAV LINKS */}
          <ul className="sidebar-nav">
            {getVisibleNavItems(moveToFront(getRoleNavItems(), "/visun-ai")).map((item) => (
              <li key={item.path}>
                {/* If item has children, render as expandable menu */}
                {item.children ? (
                  <>
                    <button
                      className={`nav-link nav-parent ${!isOpen ? "mini" : ""}`}
                      onClick={() => toggleMenu(item.label)}
                      title={!isOpen ? item.label : ""}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {isOpen && (
                        <>
                          <span className="nav-label">{item.label}</span>
                          <span className="nav-chevron">
                            {expandedMenus[item.label] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </span>
                        </>
                      )}
                    </button>
                    {/* Child items */}
                    {(isOpen && expandedMenus[item.label]) && (
                      <ul className="nav-children">
                        {item.children
                          .filter(child => {
                            if (role === "admin") return true;
                            const childViewAllowed = child.viewKey ? allowedViewSet.has(child.viewKey) : true;
                            if (role !== "client" && !childViewAllowed) return false;
                            return child.alwaysShow || unlockedFeatures[child.id];
                          })
                          .map((child, childIdx) => (
                            <li key={childIdx}>
                              <NavLink
                                to={child.path}
                                onClick={handleNavClick}
                                className={({ isActive }) =>
                                  `nav-link nav-child ${isActive ? "active" : ""}`
                                }
                              >
                                <span className="nav-icon">{child.icon}</span>
                                <span className="nav-label">{child.label}</span>
                              </NavLink>
                            </li>
                          ))}
                      </ul>
                    )}
                  </>
                ) : (
                  /* Regular nav item */
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""} ${!isOpen ? "mini" : ""}`
                    }
                    title={!isOpen ? item.label : ""}
                    end
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {isOpen && <span className="nav-label">{item.label}</span>}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom spacer */}
        <div style={{ paddingBottom: '10px' }} />
      </aside>

      {isAlertsOpen && (
        <section
          ref={alertsPanelRef}
          className={`notification-split-panel ${isOpen ? "sidebar-open" : "sidebar-closed"}`}
          aria-label="Notifications panel"
        >
          <div className="notification-split-header">
            <span>Notifications</span>
            <div className="notification-split-header-actions">
              <span className="notification-header-count">{alerts.length}</span>
              <button
                type="button"
                className="notification-show-all-btn"
                onClick={() => setShowAllAlerts((prev) => !prev)}
              >
                {showAllAlerts ? "Last 14h" : "Show all"}
              </button>
              {hasActiveSubscription && (
                <button
                  type="button"
                  className="notification-settings-btn"
                  onClick={() => setIsNotificationSettingsOpen(true)}
                  aria-label="Notification settings"
                  title="Notification types"
                >
                  <Settings size={14} />
                </button>
              )}
              <button
                type="button"
                className="notification-split-close"
                onClick={() => {
                  setIsNotificationSettingsOpen(false);
                  setShowAllAlerts(false);
                  setIsAlertsOpen(false);
                }}
                aria-label="Close notifications"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="notification-split-body">
            {alertsLoading ? (
              <div className="notification-empty">Loading alerts...</div>
            ) : visibleAlerts.length === 0 ? (
              <div className="notification-empty">
                {showAllAlerts ? "No alerts available" : "No notifications in the last 14 hours"}
              </div>
            ) : (
              visibleAlerts.map((alert, idx) => (
                <button
                  type="button"
                  key={alert._id || `${alert.agentId}-${idx}`}
                  className={`notification-item ${getAlertTimestamp(alert) > alertsLastSeenAt ? "unread" : ""}`}
                  onClick={() => handleAlertItemClick(alert)}
                >
                  <span className={`notification-item-icon ${getSeverityClass(alert.severity)}`}>
                    <AlertTriangle size={14} />
                  </span>
                  <div className="notification-item-content">
                    <div className="notification-item-row">
                      <div className="notification-item-title" title={alert.url || "Threat detected"}>
                        {alert.type || alert.url || "Threat detected"}
                      </div>
                      <span className="notification-item-time">{formatRelativeTime(alert)}</span>
                    </div>
                    <div
                      className="notification-item-subtitle"
                      title={alert.description || alert.title || "Suspicious activity detected"}
                    >
                      {alert.description || alert.title || "Suspicious activity detected"}
                    </div>
                    <div className="notification-item-meta">
                      {alert.agentId || "Unknown agent"} | {alert.severity || "High"}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </section>
      )}

      {selectedAlert && (
        <div className="notification-detail-overlay" onClick={() => setSelectedAlert(null)}>
          <section
            className="notification-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Notification details"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="notification-detail-header">
              <div className={`notification-detail-severity ${getSeverityClass(selectedAlert.severity)}`}>
                {selectedAlert.severity || "High"}
              </div>
              <button
                type="button"
                className="notification-split-close"
                onClick={() => setSelectedAlert(null)}
                aria-label="Close notification details"
              >
                <X size={16} />
              </button>
            </div>
            <div className="notification-detail-title">
              {selectedAlert.type || selectedAlert.url || "Threat detected"}
            </div>
            <div className="notification-detail-body">
              {selectedAlert.description || selectedAlert.title || "Suspicious activity detected"}
            </div>
            <div className="notification-detail-meta">
              <div>Agent: {selectedAlert.agentId || "Unknown agent"}</div>
              <div>Time: {formatAlertTime(selectedAlert)}</div>
            </div>
            <div className="notification-detail-actions">
              <button type="button" className="notification-detail-btn secondary" onClick={() => setSelectedAlert(null)}>
                Close
              </button>
              <button type="button" className="notification-detail-btn primary" onClick={handleAlertNavigate}>
                Navigate
              </button>
            </div>
          </section>
        </div>
      )}

      {isNotificationSettingsOpen && (
        <div className="notification-detail-overlay" onClick={() => setIsNotificationSettingsOpen(false)}>
          <section
            className="notification-settings-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Notification type settings"
            ref={notificationSettingsModalRef}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="notification-detail-header">
              <div className="notification-settings-title">Notification Center</div>
              <button
                type="button"
                className="notification-split-close"
                onClick={() => setIsNotificationSettingsOpen(false)}
                aria-label="Close notification settings"
              >
                <X size={16} />
              </button>
            </div>


            <div className="notification-settings-grid">
              {/* Left Column: Scheduled Report */}
              <div className="ns-section">
                <div className="ns-section-title">Scheduled Report</div>
                <div className="notification-report-card">
                  <div className="notification-report-row main-toggle">
                    <label className="notification-report-toggle">
                      <input
                        type="checkbox"
                        checked={!!reportSchedule.enabled}
                        onChange={(event) => {
                          const nextEnabled = !!event.target.checked;
                          setReportSchedule((prev) => ({ ...prev, enabled: nextEnabled }));
                        }}
                        disabled={reportScheduleSaving}
                      />
                      <span>Enable daily productivity email</span>
                    </label>
                  </div>

                  <div className="notification-report-row settings-grid">
                    <label className="notification-report-field">
                      <span>Recipient Email</span>
                      <input
                        type="email"
                        value={reportRecipientEmail}
                        placeholder={email || "reports@company.com"}
                        onChange={(event) => {
                          setReportRecipientEmail(String(event.target.value || "").trim().toLowerCase());
                        }}
                        disabled={reportScheduleSaving}
                      />
                    </label>
                    <label className="notification-report-field">
                      <span>Delivery Time</span>
                      <input
                        type="time"
                        value={reportSchedule.time}
                        onChange={(event) => {
                          const nextTime = String(event.target.value || "").trim();
                          setReportSchedule((prev) => ({ ...prev, time: nextTime }));
                        }}
                        disabled={!reportSchedule.enabled || reportScheduleSaving}
                      />
                    </label>
                  </div>

                  <div className="notification-report-actions">
                    <button
                      type="button"
                      className="notification-report-save-btn"
                      onClick={handleSaveDailyReportSchedule}
                      disabled={reportScheduleSaving}
                    >
                      {reportScheduleSaving ? "Saving..." : "Save Settings"}
                    </button>
                  </div>

                  {reportScheduleEligibility?.message && (
                    <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '8px', lineHeight: '1.4' }}>
                      {reportScheduleEligibility.message}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Global Switch + Individual Alerts */}
              <div className="ns-section">
                <div className="ns-section-title">Email Dispatch</div>

                {/* Master Switch Moved Here */}
                <div className="ns-master-toggle">
                  <div className="ns-master-info">
                    <span className="ns-master-label">Master Switch</span>
                    <span className="ns-master-desc">
                      {emailMasterEnabled ? "Notifications ON" : "Notifications OFF"}
                    </span>
                  </div>
                  <div
                    className={`ns-switch ${emailMasterEnabled ? "active" : ""} ${emailMasterSaving ? "loading" : ""}`}
                    onClick={handleToggleEmailMaster}
                  >
                    <div className="ns-switch-handle" />
                  </div>
                </div>

                <div className="ns-divider-small" />

                <div className="notification-settings-tools">
                  <div className="email-master-recipient">
                    <Mail size={12} style={{ opacity: 0.7 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {reportRecipientEmail || email}
                    </span>
                  </div>
                  <div className="ns-tools-row">
                    <button
                      type="button"
                      className="notification-select-all-btn"
                      onClick={handleSelectAllSubscriptions}
                      disabled={subscriptionSavingType === "all"}
                    >
                      <Check size={14} />
                      <span>{subscriptionSavingType === "all" ? "..." : "Select All"}</span>
                    </button>
                    <button
                      type="button"
                      className="notification-simulate-btn"
                      onClick={handleSimulateAllNotifications}
                      disabled={simulatingNotifications}
                    >
                      <Bell size={14} />
                      <span>{simulatingNotifications ? "..." : "Test Email"}</span>
                    </button>
                  </div>
                </div>

                <div className="notification-types-grid">
                  {notificationTypeRows.map((row) => (
                    <div key={row.type} className="notification-type-item">
                      <div className="type-label">
                        <span className="type-name">{row.type}</span>
                        <span className="type-count">{row.count} alerts</span>
                      </div>
                      <label className="inline-toggle">
                        <input
                          type="checkbox"
                          checked={!!row.subscribed}
                          onChange={() => handleToggleSubscription(row.type, !row.subscribed)}
                          disabled={subscriptionSavingType === row.type || subscriptionSavingType === "all"}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

    </>
  );
};

export default Sidebar;
